import { useMemo, useCallback, useState, useEffect } from 'react';

import { useTranslation } from 'react-i18next';

import {
  formatValue,
  useGetDashboardLocationsQuery,
  useGetWarehouseQuery,
  computeLocationParams,
  type DashboardOptions
} from '@/entities/dashboard';

import { useProjectSiteParams, useUrlState } from '@/shared/lib';
import type { DateRangeValue } from '@/shared/ui';

import { buildStorageFilterChips } from '../model/build-filter-chips';
import type { StorageTab, StorageUrlState } from '../model/types';

const DEFAULT_URL_STATE: StorageUrlState = {
  tab: 'soh',
  metric: 'W',
  group: 'D',
  sohMetric: 'W',
  sohGroup: 'D',
  period: 'L6M',
  fromDate: '',
  toDate: ''
};

/**
 * Which URL key a filter is stored under on each tab. The panel and the API both
 * speak the plain names, so the tab is resolved here and nowhere else.
 */
const TAB_KEYS = {
  movements: { metric: 'metric', group: 'group' },
  soh: { metric: 'sohMetric', group: 'sohGroup' }
} as const satisfies Record<StorageTab, Record<'metric' | 'group', keyof StorageUrlState>>;

export const useStorageOverview = () => {
  const { t } = useTranslation();
  const { projectId, siteId } = useProjectSiteParams();
  const [urlState, setUrlState] = useUrlState(DEFAULT_URL_STATE);
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);

  const { data: locationsData, isLoading: locationsLoading } = useGetDashboardLocationsQuery(
    { projectId },
    { skip: !projectId }
  );

  const locations = useMemo(
    () =>
      locationsData?.locationGroups?.flatMap((group) => group.locations.map(({ id, name }) => ({ id, name }))) ?? [],
    [locationsData]
  );

  const allLocationIds = useMemo(() => locations.map((location) => location.id), [locations]);

  const tab: StorageTab = urlState.tab === 'movements' ? 'movements' : 'soh';
  const keys = TAB_KEYS[tab];
  // The date range is a Stock-movements filter, so the Stock-on-hand tab asks for
  // the default range rather than inheriting one it never offered to set.
  const showDateRange = tab === 'movements';

  /**
   * The active tab's filters under the plain names the panel and the API use.
   * Everything downstream reads this rather than the raw URL state, so nothing
   * else has to know that the values live under two sets of keys.
   */
  const filters: StorageUrlState = useMemo(
    () => ({
      tab,
      metric: urlState[keys.metric] as StorageUrlState['metric'],
      group: urlState[keys.group] as StorageUrlState['group'],
      period: showDateRange ? urlState.period : DEFAULT_URL_STATE.period,
      fromDate: showDateRange ? urlState.fromDate : DEFAULT_URL_STATE.fromDate,
      toDate: showDateRange ? urlState.toDate : DEFAULT_URL_STATE.toDate
    }),
    [tab, keys, showDateRange, urlState]
  );

  const warehouseParams: DashboardOptions = useMemo(() => {
    const locationParams = computeLocationParams(locationsData?.locationGroups, selectedLocations);
    return {
      ...filters,
      ...locationParams,
      projectId,
      siteId
    };
  }, [filters, selectedLocations, locationsData?.locationGroups, projectId, siteId]);

  const { data, isLoading, isFetching, error, refetch } = useGetWarehouseQuery(warehouseParams);

  const updateOption = useCallback(
    (key: keyof DashboardOptions | 'tab', value: string) => {
      const tabKey = key === 'metric' || key === 'group' ? keys[key] : key;
      setUrlState({ [tabKey]: value });
    },
    [setUrlState, keys]
  );

  const handleDateRangeChange = useCallback(
    (dateRange: DateRangeValue) => {
      setUrlState(dateRange as StorageUrlState);
    },
    [setUrlState]
  );

  const formattedStats = useMemo(() => {
    if (!data?.stats) return null;

    return {
      totalVolume: formatValue(data.stats.totalVolume, filters.metric),
      totalReceived: formatValue(data.stats.totalReceived, filters.metric),
      longStandingPerc: `${data.stats.longStandingPerc.toFixed(1)}%`,
      expiringSOH: formatValue(data.stats.expiringSOH, filters.metric),
      lossTotal: formatValue(data.stats.lossTotal, filters.metric),
      damageTotal: formatValue(data.stats.damageTotal, filters.metric)
    };
  }, [data?.stats, filters.metric]);

  const resetLocations = useCallback(() => setSelectedLocations(allLocationIds), [allLocationIds]);

  const removeFilter = useCallback(
    (key: string) => {
      if (key === 'date') {
        setUrlState({
          period: DEFAULT_URL_STATE.period,
          fromDate: DEFAULT_URL_STATE.fromDate,
          toDate: DEFAULT_URL_STATE.toDate
        });
        return;
      }

      const tabKey = (key === 'metric' || key === 'group' ? keys[key] : key) as keyof StorageUrlState;
      setUrlState({ [tabKey]: DEFAULT_URL_STATE[tabKey] });
    },
    [setUrlState, keys]
  );

  /**
   * Reset clears what the panel is showing, which is the active tab's filters —
   * the other tab's choices are not on screen to be reset, and wiping them would
   * also leave the button enabled by filters the reader cannot see.
   */
  const handleResetFilters = useCallback(() => {
    setUrlState({
      [keys.metric]: DEFAULT_URL_STATE[keys.metric],
      [keys.group]: DEFAULT_URL_STATE[keys.group],
      ...(showDateRange
        ? {
            period: DEFAULT_URL_STATE.period,
            fromDate: DEFAULT_URL_STATE.fromDate,
            toDate: DEFAULT_URL_STATE.toDate
          }
        : {})
    });

    resetLocations();
  }, [setUrlState, keys, showDateRange, resetLocations]);

  const activeFilters = useMemo(
    () =>
      buildStorageFilterChips({
        state: filters,
        defaults: DEFAULT_URL_STATE,
        t,
        locations,
        selectedLocations,
        showDateRange,
        onRemove: removeFilter,
        onResetLocations: resetLocations
      }),
    [filters, t, locations, selectedLocations, showDateRange, removeFilter, resetLocations]
  );

  const activeFiltersCount = activeFilters.length;

  // Select all locations by default when location data first loads or project/site changes
  useEffect(() => {
    setSelectedLocations(allLocationIds);
  }, [allLocationIds, projectId, siteId]);

  return {
    // Data
    data,
    formattedStats,
    locationsData,

    // Loading states
    isLoading,
    isFetching,
    locationsLoading,
    error,

    // Filters — `filters` carries the active tab's values under the plain names;
    // `urlState` is the raw two-tab store and is not what the page should read.
    urlState: filters,
    selectedLocations,
    locations,
    showDateRange,
    activeFilters,
    activeFiltersCount,
    hasActiveFilters: activeFiltersCount > 0,

    // Project/Site
    projectId,
    siteId,

    // Handlers
    updateOption,
    handleDateRangeChange,
    setSelectedLocations,
    removeFilter,
    refetch,
    resetFilters: handleResetFilters
  };
};
